import React from 'react';

import {
  Link,
  RecordContextProvider,
  TextField,
  useGetOne,
  useRecordContext,
} from 'react-admin';

import { Ping } from '../../../utilities/schemas/ping';
import { Rule } from '../../../utilities/schemas/rules';

import ruleNames, { RuleNames } from '../../../constants/ruleNames';
import useDialogStatus from '../../../hooks/useDialogStatus';
import EntityOverlay from '../../entities/EntityOverlay';

const mapRuleNamePathToLink: Record<RuleNames, string | null> = {
  [ruleNames.ADDITIONAL_DELIVERY_ADDRESS]: null,
  [ruleNames.ADVERSE_MEDIA]: 'kyc-data/adverse-media',
  [ruleNames.AFFORDABILITY]: null,
  [ruleNames.AGE_THRESHOLD]: null,
  [ruleNames.ALWAYS_TRIGGER]: null,
  [ruleNames.AMOUNT_THRESHOLD]: null,
  [ruleNames.ANOMALY_DETECTION]: null,
  [ruleNames.ANOMALY_DETECTION_INCOMING_AVERAGE]: null,
  [ruleNames.ANOMALY_DETECTION_INCOMING_FREQUENCY]: null,
  [ruleNames.ANOMALY_DETECTION_INCOMING_SINGLE_AMOUNT]: null,
  [ruleNames.ANOMALY_DETECTION_INCOMING_VOLUME_ROLLING_SUM]: null,
  [ruleNames.ANOMALY_DETECTION_OUTGOING_AVERAGE]: null,
  [ruleNames.ANOMALY_DETECTION_OUTGOING_FREQUENCY]: null,
  [ruleNames.ANOMALY_DETECTION_OUTGOING_SINGLE_AMOUNT]: null,
  [ruleNames.ANOMALY_DETECTION_OUTGOING_VOLUME_ROLLING_SUM]: null,
  [ruleNames.ANOMALY_DETECTION_PROCESSING_AVERAGE]: null,
  [ruleNames.ANOMALY_DETECTION_PROCESSING_FREQUENCY]: null,
  [ruleNames.ANOMALY_DETECTION_PROCESSING_SINGLE_AMOUNT]: null,
  [ruleNames.ANOMALY_DETECTION_PROCESSING_VOLUME_ROLLING_SUM]: null,
  [ruleNames.ANOMALY_DETECTION_TOTAL_AVERAGE]: null,
  [ruleNames.ANOMALY_DETECTION_TOTAL_FREQUENCY]: null,
  [ruleNames.ANOMALY_DETECTION_TOTAL_SINGLE_AMOUNT]: null,
  [ruleNames.ANOMALY_DETECTION_TOTAL_VOLUME_ROLLING_SUM]: null,
  [ruleNames.BUSINESS_ACTIVE]: null,
  [ruleNames.BUSINESS_AUDITOR]: null,
  [ruleNames.BUSINESS_DATA_CHANGED]: null,
  [ruleNames.BUSINESS_EQUITY_SHARE]: null,
  [ruleNames.BUSINESS_EQUITY]: null,
  [ruleNames.BUSINESS_FINANCIAL_STATEMENT_COMMENT]: null,
  [ruleNames.BUSINESS_FTAX]: null,
  [ruleNames.BUSINESS_PAYMENT_APPLICATIONS_SUM]: null,
  [ruleNames.BUSINESS_PAYMENT_APPLICATIONS]: null,
  [ruleNames.BUSINESS_PAYMENT_REMARKS_SUM]: null,
  [ruleNames.BUSINESS_PAYMENT_REMARKS]: null,
  [ruleNames.BUSINESS_PROVIDER_WARNING]: null,
  [ruleNames.BUSINESS_SANCTION_LIST]: 'kyc-data/sanction-list',
  [ruleNames.BUSINESS_VAT]: null,
  [ruleNames.CAR_FREQ_HISTORICAL_OWNERS]: null,
  [ruleNames.CAR_GUARANTOR_RELATED]: null,
  [ruleNames.CAR_IN_CIRCULATION]: null,
  [ruleNames.CAR_VALUATION]: null,
  [ruleNames.CASH_FLOW]: null,
  [ruleNames.CATEGORIZATION_AMOUNT_THRESHOLD]: null,
  [ruleNames.CO_ADDRESS]: null,
  [ruleNames.COLLECTOR]: null,
  [ruleNames.CONNECTED_BUYER_SELLER]: null,
  [ruleNames.CONNECTED_ENTITY_PEP]: null,
  [ruleNames.CONNECTED_ENTITY_SANCTION_LIST]: null,
  [ruleNames.COUNTRY_CITIZENSHIP_BLACKLIST]: null,
  [ruleNames.COUNTRY_RESIDENCE_BLACKLIST]: null,
  [ruleNames.COUNTRY_RESIDENCE_WHITELIST]: null,
  [ruleNames.CREDIT_RISK]: null,
  [ruleNames.DATAPOINT_CONFLICT]: null,
  [ruleNames.DETECT_NEW_IP]: null,
  [ruleNames.DUE_DILIGENCE]: null,
  [ruleNames.EMPLOYMENT_TYPE]: null,
  [ruleNames.ENTITY_CONNECTED_ACCOUNT_COUNT]: 'accounts',
  [ruleNames.ENTITY_DISTINCT_IP_COUNT]: null,
  [ruleNames.FOREIGN_ADDRESS]: null,
  [ruleNames.INCOMING_CATEGORIZATION_FREQUENCY]: null,
  [ruleNames.INCOMING_CATEGORIZATION_ROLLING_SUM]: null,
  [ruleNames.INCOMING_FREQUENCY]: null,
  [ruleNames.INCOMING_VOLUME_ROLLING_SUM]: null,
  [ruleNames.INDIVIDUAL_CRIMINAL_RECORD]: null,
  [ruleNames.INDIVIDUAL_PEP]: 'kyc-data/pep',
  [ruleNames.INDIVIDUAL_SANCTION_LIST]: 'kyc-data/sanction-list',
  [ruleNames.IP_ANONYMOUS]: null,
  [ruleNames.IP_COUNTRY_ALLOWLIST]: null,
  [ruleNames.IP_COUNTRY_BLACKLIST]: null,
  [ruleNames.KYC_VALID]: null,
  [ruleNames.LOW_INCOME]: null,
  [ruleNames.MERCHANT_CATEGORY_CODE_BLOCKLIST]: null,
  [ruleNames.MULTIPLE_IP_COUNTRIES]: null,
  [ruleNames.OUTGOING_CATEGORIZATION_FREQUENCY]: null,
  [ruleNames.OUTGOING_CATEGORIZATION_ROLLING_SUM]: null,
  [ruleNames.OUTGOING_FREQUENCY]: null,
  [ruleNames.OUTGOING_VOLUME_ROLLING_SUM]: null,
  [ruleNames.PROCESSING_FREQUENCY]: null,
  [ruleNames.PROCESSING_VOLUME_ROLLING_SUM]: null,
  [ruleNames.RELATED_INDIVIDUALS_CRIMINAL_RECORD]: null,
  [ruleNames.SELLER_OWNS_CAR]: null,
  [ruleNames.SHARED_IP_ENTITY_COUNT]: null,
  [ruleNames.SUSTAINED_FOREIGN_IP_COUNTRY]: null,
  [ruleNames.SNI_CODE_BLACKLIST]: null,
  [ruleNames.SOURCE_OF_FUNDS]: null,
  [ruleNames.SOURCE_OF_INCOME]: null,
  [ruleNames.SPAR_CHANGED]: null,
  [ruleNames.SUSPICIOUS_FUNDS]: null,
  [ruleNames.TEST_BLOCK]: null,
  [ruleNames.TEST_REVIEW]: null,
  [ruleNames.TIME_WINDOW]: null,
  [ruleNames.TOTAL_CATEGORIZATION_FREQUENCY]: null,
  [ruleNames.TOTAL_CATEGORIZATION_ROLLING_SUM]: null,
  [ruleNames.TOTAL_FREQUENCY]: null,
  [ruleNames.TOTAL_VOLUME_ROLLING_SUM]: null,
  [ruleNames.TRANSACTION_NAME_MATCHING]: null,
  [ruleNames.INVOICE_PAID_BY_ISSUER]: null,
  [ruleNames.TRANSACTION_MONITORING_TYPICAL_AMOUNT]: null,
  [ruleNames.ZIP_CODE_BLACKLIST]: null,
};

const PingDescription = ({
  caseId,
  source,
}: {
  caseId: string;
  source: string;
  sortable?: boolean;
}) => {
  const record = useRecordContext<Ping>();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const { data, isLoading } = useGetOne<Rule>('rules', { id: `${record?.rule}` }, { enabled: !!record?.rule });

  const linkPath = data?.name ? mapRuleNamePathToLink[data?.name] : null;

  if (!record) return null;

  if (isLoading) {
    return (
      <TextField source={source} />
    );
  }

  if (linkPath && record.entities[0]) {
    return (
      <>
        <RecordContextProvider value={record.entities[0]}>
          <EntityOverlay
            defaultEntries={linkPath}
            open={open}
            closeDialog={closeDialog}
            caseId={caseId}
          />
        </RecordContextProvider>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          to=""
          onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            openDialog();
          }}
        >
          <TextField source={source} />
        </Link>
      </>
    );
  }

  return (
    <TextField source={source} />
  );
};

export default PingDescription;
